import * as React from "react"
import { useState } from "react"
import { mdiCheck, mdiClose, mdiUnfoldMoreHorizontal } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { cn } from "../lib/utils"
import { Button } from "./ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./ui/command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"

export interface SearchableSelectOption {
  value: string
  label: string
  /** Optional MDI icon path rendered before the label in both the trigger and the item. */
  icon?: string
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string
  placeholder?: string
  emptyMessage?: string
  searchPlaceholder?: string
  onValueChange?: (value: string) => void
  className?: string
  disabled?: boolean
  loading?: boolean
  'data-testid'?: string
}

export function SearchableSelect({
  options,
  value,
  placeholder = "Select option...",
  emptyMessage = "No results found.",
  searchPlaceholder = "Search...",
  onValueChange,
  className,
  disabled = false,
  loading = false,
  'data-testid': dataTestId,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")

  const selectedOption = options.find((option) => option.value === value)

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
    option.value.toLowerCase().includes(searchValue.toLowerCase())
  )

  const handleSelect = (selectedValue: string) => {
    if (selectedValue === value) {
      onValueChange?.("")
    } else {
      onValueChange?.(selectedValue)
    }
    setOpen(false)
    setSearchValue("")
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onValueChange?.("")
  }

  // The X lives inside the DropdownMenuTrigger's Button (asChild), and Radix
  // opens the menu on `pointerdown` on that trigger element — which fires
  // and bubbles *before* `onClick`, so by the time handleClear's
  // stopPropagation runs the menu has already toggled open. Stop the pointer
  // event here instead, before it reaches Radix's trigger handler, so
  // clicking the X only clears the value and never opens the popover.
  const handleClearPointerDown = (e: React.PointerEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled || loading}
          data-testid={dataTestId}
        >
          <span className="flex items-center gap-1.5 truncate">
            {!loading && selectedOption?.icon && (
              <AppIcon path={selectedOption.icon} size="xs" className="shrink-0" />
            )}
            <span className="truncate">
              {loading
                ? "Loading..."
                : selectedOption
                ? selectedOption.label
                : placeholder}
            </span>
          </span>
          <div className="flex items-center gap-1">
            {value && !disabled && !loading && (
              // Button's own utilities set `[&_svg]:pointer-events-none` on every
              // nested svg (so the whole button is one click target) with a
              // descendant selector that out-specificities a plain
              // `pointer-events-auto` class. An inline style is the only thing
              // that reliably wins here, which is what lets this affordance
              // receive its own pointer/click events instead of being
              // transparent to them.
              <span
                role="button"
                aria-label="Clear"
                data-testid="searchable-select-clear"
                className="inline-flex shrink-0 opacity-50 hover:opacity-100"
                style={{ pointerEvents: "auto" }}
                onPointerDown={handleClearPointerDown}
                onClick={handleClear}
              >
                <AppIcon path={mdiClose} size="sm" />
              </span>
            )}
            <AppIcon path={mdiUnfoldMoreHorizontal} size="sm" tone="muted" className="shrink-0 opacity-50" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
        {/* bg-popover: DropdownMenuContent (this wrapper) is not part of the
            spec 2026-09-22-04 bg-card unification — it still resolves
            bg-popover — so Command must match it explicitly here instead of
            falling back to its own now-bg-card default, or the two would
            show a visible seam in dark mode. */}
        <Command shouldFilter={false} className="bg-popover">
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchValue}
            onValueChange={setSearchValue}
            data-testid={dataTestId ? `${dataTestId}-search` : undefined}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => handleSelect(option.value)}
                  className="cursor-pointer"
                >
                  <AppIcon path={mdiCheck} size="sm" className={cn( "mr-2 ", value === option.value ? "opacity-100" : "opacity-0" )} />
                  {option.icon && (
                    <AppIcon path={option.icon} size="xs" className="mr-1.5 shrink-0" />
                  )}
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}